import ComputerIcon from '@mui/icons-material/Computer'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Box, Card, CardContent, IconButton, Typography } from '@mui/material'
import React from 'react'

import { DeskListItemProps } from '../../types/ComponentProps'
import Database from '../../types/Database'
import { generateColorFromString } from '../../utils/FormatHelpers'

export default function DeskListItem<T extends Database.Desk = Database.Desk>({
  desk,
  isSelected,
  readOnly,
  onDeskClick,
  onDeskDelete,
  onDeskEdit,
  bookingsForDesk
}: DeskListItemProps<T>) {
  // Check if this desk has availability info and is unavailable
  const isUnavailable =
    'isAvailable' in desk && (desk as any).isAvailable === false

  const handleDeskClick = () => {
    if (isUnavailable) return
    onDeskClick(desk)
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onDeskDelete && desk.id) {
      onDeskDelete(desk.id)
    }
  }

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onDeskEdit) {
      onDeskEdit(desk)
    }
  }

  const renderLabels = () => {
    if (!desk.labels || desk.labels.trim().length === 0) {
      return null
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
        {desk.labels.split(',').map((label, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: '12px',
              backgroundColor: generateColorFromString(label ?? ''),
              fontSize: '0.75rem',
              fontWeight: 500,
              color: '#ffffff'
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                flexShrink: 0
              }}
            />
            {label.trim()}
          </Box>
        ))}
      </Box>
    )
  }

  const renderBookings = () => {
    if (!isUnavailable || !bookingsForDesk || bookingsForDesk.length === 0) {
      return null
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
        {bookingsForDesk.map((booking: Database.Booking, index: number) => {
          // Handle both Date objects and Firestore Timestamps
          let date: Date | null = null
          if (booking.forDate) {
            if (booking.forDate instanceof Date) {
              date = booking.forDate
            } else if (
              typeof booking.forDate === 'object' &&
              'toDate' in booking.forDate
            ) {
              // Firestore Timestamp
              date = (booking.forDate as any).toDate()
            }
          }

          const formattedDate = date
            ? `${String(date.getDate()).padStart(2, '0')}/${String(
                date.getMonth() + 1
              ).padStart(2, '0')}`
            : ''

          return (
            <Box
              key={index}
              className='booking-badge'
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: '12px',
                fontSize: '0.7rem',
                fontWeight: 600
              }}
            >
              {formattedDate} {booking.bookerName}
            </Box>
          )
        })}
      </Box>
    )
  }

  return (
    <Card
      sx={{
        height: 'auto',
        cursor: isUnavailable ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease-in-out',
        border: isSelected ? '2px solid' : 'none',
        borderColor: isSelected ? 'primary.main' : 'transparent',
        backgroundColor: isUnavailable
          ? theme =>
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.02)'
                : 'rgba(0, 0, 0, 0.04)'
          : 'background.paper',
        opacity: isUnavailable ? 0.7 : 1,
        borderRadius: 2,
        '&:hover': !isUnavailable
          ? {
              transform: 'translateY(-1px)',
              boxShadow: theme =>
                theme.palette.mode === 'dark'
                  ? '0 0 24px rgba(56, 189, 248, 0.15), 0 8px 16px rgba(0, 0, 0, 0.4)'
                  : '0 4px 12px rgba(0,0,0,0.15)',
              borderColor: 'primary.main',
              opacity: 1
            }
          : {
              opacity: 0.85
            }
      }}
      onClick={handleDeskClick}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {readOnly ? (
            <ComputerIcon
              sx={{
                color: 'text.secondary',
                fontSize: 20,
                mt: 0.25,
                flexShrink: 0
              }}
            />
          ) : (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: isSelected
                  ? 'primary.main'
                  : isUnavailable
                  ? 'action.disabled'
                  : 'primary.main',
                backgroundColor: isSelected ? 'primary.main' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mt: 0.25
              }}
            >
              {isSelected && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: 'primary.contrastText'
                  }}
                />
              )}
            </Box>
          )}

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: desk.labels && desk.labels.trim().length > 0 ? 1 : 0
              }}
            >
              {desk.officeName}-{desk.name}
            </Typography>

            {renderLabels()}
            {renderBookings()}
          </Box>

          {(onDeskDelete || onDeskEdit) && (
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              {onDeskDelete && (
                <IconButton
                  size='small'
                  onClick={handleDeleteClick}
                  sx={{
                    color: 'error.main',
                    '&:hover': {
                      backgroundColor: 'error.main',
                      color: 'error.contrastText'
                    }
                  }}
                >
                  <DeleteIcon fontSize='small' />
                </IconButton>
              )}
              {onDeskEdit && (
                <IconButton
                  size='small'
                  onClick={handleEditClick}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText'
                    }
                  }}
                >
                  <EditIcon fontSize='small' />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}
